import { useState, useEffect, useMemo, useCallback } from 'react';
import { ChatRoom } from '../types';
import { SearchFilterOptions } from '../components/chat/Sidebar/SearchFilters';

export interface UseAdvancedSearchOptions {
  data: ChatRoom[];
  debounceMs?: number;
  enableHighlighting?: boolean;
}

export interface SearchResult {
  item: ChatRoom;
  score: number;
  matchedFields: string[];
  highlightedContent?: { [key: string]: string };
}

export const useAdvancedSearch = ({
  data,
  debounceMs = 300,
  enableHighlighting = true,
}: UseAdvancedSearchOptions) => {
  const [filters, setFilters] = useState<SearchFilterOptions>({
    searchTerm: '',
    dateRange: { start: null, end: null },
    status: [],
    priority: [],
    tags: [],
    assignedTo: [],
    messageCount: { min: 0, max: 100 },
    responseTime: { min: 0, max: 24 },
    quickFilters: [],
  });

  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [isSearching, setIsSearching] = useState(false);

  // 検索語のデバウンス処理
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(filters.searchTerm);
      setIsSearching(false);
    }, debounceMs);

    if (filters.searchTerm !== debouncedSearchTerm) {
      setIsSearching(true);
    }

    return () => clearTimeout(timer);
  }, [filters.searchTerm, debounceMs, debouncedSearchTerm]);

  // テキストマッチング関数
  const getTextScore = useCallback((text: string, searchTerm: string): number => {
    if (!text || !searchTerm) return 0;
    
    const lowerText = text.toLowerCase();
    const lowerSearch = searchTerm.toLowerCase();
    
    // 完全一致
    if (lowerText === lowerSearch) return 100;
    
    // 前方一致
    if (lowerText.startsWith(lowerSearch)) return 80;
    
    // 単語境界での一致
    const wordBoundaryRegex = new RegExp(`\\b${lowerSearch}\\b`, 'i');
    if (wordBoundaryRegex.test(text)) return 70;
    
    // 部分一致
    if (lowerText.includes(lowerSearch)) return 50;
    
    // ファジーマッチング（簡易版）
    const fuzzyScore = getFuzzyScore(lowerText, lowerSearch);
    if (fuzzyScore > 0.7) return Math.floor(fuzzyScore * 40);
    
    return 0;
  }, []);

  // ファジーマッチング関数
  const getFuzzyScore = (text: string, pattern: string): number => {
    if (pattern.length === 0) return 1;
    if (text.length === 0) return 0;

    let patternIdx = 0;
    let textIdx = 0;
    let matches = 0;

    while (textIdx < text.length && patternIdx < pattern.length) {
      if (text[textIdx] === pattern[patternIdx]) {
        matches++;
        patternIdx++;
      }
      textIdx++;
    }

    return matches / pattern.length;
  };

  // テキストハイライト関数
  const highlightText = useCallback((text: string, searchTerm: string): string => {
    if (!searchTerm || !enableHighlighting) return text;
    
    const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    return text.replace(regex, '<mark>$1</mark>');
  }, [enableHighlighting]);

  // 日付フィルター
  const matchesDateRange = useCallback((chat: ChatRoom): boolean => {
    if (!filters.dateRange.start && !filters.dateRange.end) return true;
    
    const chatDate = new Date(chat.created_at);
    
    if (filters.dateRange.start && chatDate < filters.dateRange.start) return false;
    if (filters.dateRange.end && chatDate > filters.dateRange.end) return false;
    
    return true;
  }, [filters.dateRange]);

  // クイックフィルター
  const matchesQuickFilters = useCallback((chat: ChatRoom): boolean => {
    if (filters.quickFilters.length === 0) return true;
    
    return filters.quickFilters.every(filter => {
      switch (filter) {
        case 'unread':
          return (chat.unread_count || 0) > 0;
        case 'urgent':
          return chat.priority === 'high' || chat.tags?.includes('urgent');
        case 'today':
          const today = new Date();
          const chatDate = new Date(chat.created_at);
          return chatDate.toDateString() === today.toDateString();
        case 'no-response':
          // last_message_typeプロパティが存在しないため、仮の条件を使用
          return chat.status === 'active' && (chat.last_message?.length || 0) > 0;
        case 'long-wait':
          const lastMessageTime = new Date(chat.updated_at);
          const hoursSinceLastMessage = (Date.now() - lastMessageTime.getTime()) / (1000 * 60 * 60);
          return hoursSinceLastMessage > 2;
        default:
          return true;
      }
    });
  }, [filters.quickFilters]);

  // メッセージ数フィルター
  const matchesMessageCount = useCallback((chat: ChatRoom): boolean => {
    // message_countプロパティが存在しないため、仮の値を使用
    const messageCount = 10; // 仮の値（実際のAPIレスポンスに応じて調整）
    return messageCount >= filters.messageCount.min && messageCount <= filters.messageCount.max;
  }, [filters.messageCount]);

  // 検索とフィルタリングの実行
  const searchResults = useMemo(() => {
    let results: SearchResult[] = data.map(chat => ({
      item: chat,
      score: 0,
      matchedFields: [],
      highlightedContent: {},
    }));

    // テキスト検索
    if (debouncedSearchTerm) {
      results = results.map(result => {
        const chat = result.item;
        let totalScore = 0;
        const matchedFields: string[] = [];
        const highlightedContent: { [key: string]: string } = {};

        // 検索対象フィールド
        const searchFields = [
          { field: 'title', value: chat.title, weight: 3 },
          { field: 'participant_name', value: chat.participant_name, weight: 2.5 },
          { field: 'last_message', value: chat.last_message, weight: 2 },
          { field: 'description', value: chat.description, weight: 1.5 },
        ];

        searchFields.forEach(({ field, value, weight }) => {
          if (value) {
            const score = getTextScore(value, debouncedSearchTerm);
            if (score > 0) {
              totalScore += score * weight;
              matchedFields.push(field);
              highlightedContent[field] = highlightText(value, debouncedSearchTerm);
            }
          }
        });

        return {
          ...result,
          score: totalScore,
          matchedFields,
          highlightedContent,
        };
      });

      // スコアでソート（スコアが0より大きいもののみ）
      results = results.filter(result => result.score > 0);
      results.sort((a, b) => b.score - a.score);
    }

    // その他のフィルターを適用
    results = results.filter(result => {
      const chat = result.item;

      // ステータスフィルター
      if (filters.status.length > 0 && !filters.status.includes(chat.status)) {
        return false;
      }

      // 優先度フィルター
      if (filters.priority.length > 0 && !filters.priority.includes(chat.priority || 'medium')) {
        return false;
      }

      // タグフィルター
      if (filters.tags.length > 0) {
        const chatTags = chat.tags || [];
        const hasMatchingTag = filters.tags.some(tag => chatTags.includes(tag));
        if (!hasMatchingTag) return false;
      }

      // 担当者フィルター
      if (filters.assignedTo.length > 0 && !filters.assignedTo.includes(chat.created_by)) {
        return false;
      }

      // 日付範囲フィルター
      if (!matchesDateRange(chat)) return false;

      // クイックフィルター
      if (!matchesQuickFilters(chat)) return false;

      // メッセージ数フィルター
      if (!matchesMessageCount(chat)) return false;

      return true;
    });

    // 検索語がない場合は作成日時でソート
    if (!debouncedSearchTerm) {
      results.sort((a, b) => 
        new Date(b.item.updated_at).getTime() - new Date(a.item.updated_at).getTime()
      );
    }

    return results;
  }, [
    data,
    debouncedSearchTerm,
    filters,
    getTextScore,
    highlightText,
    matchesDateRange,
    matchesQuickFilters,
    matchesMessageCount,
  ]);

  // フィルター更新
  const updateFilters = useCallback((newFilters: Partial<SearchFilterOptions>) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  }, []);

  // フィルターリセット
  const resetFilters = useCallback(() => {
    setFilters({
      searchTerm: '',
      dateRange: { start: null, end: null },
      status: [],
      priority: [],
      tags: [],
      assignedTo: [],
      messageCount: { min: 0, max: 100 },
      responseTime: { min: 0, max: 24 },
      quickFilters: [],
    });
  }, []);

  // 統計情報
  const searchStats = useMemo(() => ({
    totalResults: searchResults.length,
    hasActiveFilters: 
      filters.status.length > 0 ||
      filters.priority.length > 0 ||
      filters.tags.length > 0 ||
      filters.assignedTo.length > 0 ||
      filters.quickFilters.length > 0 ||
      filters.dateRange.start !== null ||
      filters.dateRange.end !== null ||
      debouncedSearchTerm.length > 0,
    searchTerm: debouncedSearchTerm,
    isSearching,
  }), [searchResults.length, filters, debouncedSearchTerm, isSearching]);

  return {
    searchResults,
    filters,
    updateFilters,
    resetFilters,
    searchStats,
    isSearching,
    highlightText,
  };
};

export default useAdvancedSearch;
